/**
 * Hook dữ liệu của màn Cấu hình chatbot. Lưu/xoá bot làm mới cả nhánh `KHOA.bot` — danh sách quản trị, lưới bot dùng được
 * và trang trò chuyện đọc chung nhánh ấy.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { khoTroLy } from '../../../lib/repositories/tro-ly-ai.repository';
import type { BotGrant, BotMaTran, ChatbotInput } from '../types';
import { KHOA, thuLai } from './useChung';

const KHOA_MA_TRAN_BOT = [...KHOA.bot, 'quyen'] as const;

/**
 * Danh sách QUẢN TRỊ (`quan_tri=true`): mọi bot của tổ chức, kể cả bot chưa cấp cho vai nào. Khoá riêng để không ghi đè
 * danh sách đã lọc theo quyền "Xem" mà màn trò chuyện dùng.
 */
export function useBotQuanTri() {
  return useQuery({
    queryKey: KHOA.botQuanTri,
    queryFn: () => khoTroLy.danhSachBot(false, true),
    staleTime: 30_000,
    retry: thuLai,
  });
}

export function useLuuBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, du }: { id: string | null; du: ChatbotInput }) =>
      id ? khoTroLy.suaBot(id, du) : khoTroLy.taoBot(du),
    onSuccess: () => qc.invalidateQueries({ queryKey: KHOA.bot }),
  });
}

export function useXoaBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => khoTroLy.xoaBot(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KHOA.bot }),
  });
}

/** Cấp khoá nhúng MỚI — làm hỏng mọi đoạn mã đã dán bằng khoá cũ. */
export function useXoayKhoaNhung() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => khoTroLy.xoayKhoaNhung(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KHOA.botQuanTri }),
  });
}

export function useMaTranBot(bat: boolean) {
  return useQuery({
    queryKey: KHOA_MA_TRAN_BOT,
    queryFn: khoTroLy.maTranBot,
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat,
  });
}

export function useLuuMaTranBot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grants: BotGrant[]) => khoTroLy.luuMaTranBot(grants),
    onSuccess: (_, grants) => {
      // Ghi ngay bản vừa lưu vào cache: bản nháp về `null` mà không nhấp nháy về bản cũ trong lúc chờ tải lại.
      qc.setQueryData<BotMaTran>(KHOA_MA_TRAN_BOT, (cu) => (cu ? { ...cu, grants } : cu));
      return qc.invalidateQueries({ queryKey: KHOA.bot });
    },
  });
}

/** Đọc một câu mẫu bằng giọng đã chọn — WAV base64. Mỗi lần bấm là một lượt gọi TTS tính tiền. */
export function useNgheThuGiong() {
  return useMutation({ mutationFn: (giong: string) => khoTroLy.ngheThuGiong(giong) });
}
